import { Box, Chip, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import ReactECharts from "echarts-for-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useMaterialUIController } from "../../../context";
import { chartLayoutSize, normalizeChartOption } from "../../../utils/chartNormalizers";
import {
  buildForecastChartOption,
  parseForecastResult,
  type ForecastResultModel,
} from "../../../utils/forecastResultModel";

function formatNumber(n: number | undefined): string {
  if (n == null || !Number.isFinite(n)) return "—";
  return new Intl.NumberFormat(undefined, { maximumFractionDigits: 1 }).format(n);
}

export default function ForecastResultView({
  model: modelProp,
  result,
  narrativeOverride,
  message,
}: {
  model?: ForecastResultModel | null;
  result?: unknown;
  narrativeOverride?: string | null;
  message?: string;
}) {
  const { t } = useTranslation();
  const [controller] = useMaterialUIController();
  const darkMode = Boolean(controller?.darkMode);

  const model = useMemo(
    () => modelProp ?? parseForecastResult(result) ?? null,
    [modelProp, result]
  );

  const option = useMemo(() => {
    if (!model) return null;
    return normalizeChartOption(
      buildForecastChartOption(model, {
        history: t("chat.forecast.historySeries"),
        forecast: t("chat.forecast.forecastSeries"),
        value: t("chat.forecast.valueAxis"),
      })
    );
  }, [model, t]);

  if (!model) return null;

  const narrative = (narrativeOverride ?? model.narrative ?? "").trim();
  const layout = option ? chartLayoutSize(option) : { height: 280, minWidth: 320 };
  const predicted = model.nextExpected;
  const predictedLabel = model.forecastMonth
    ? t("chat.forecast.predictedFor", { period: model.forecastMonth })
    : t("chat.forecast.predictedValue");

  return (
    <Box>
      {(message || narrative) && (
        <Typography variant="body2" sx={{ mb: 1.5, whiteSpace: "pre-wrap" }}>
          {message && !narrative ? message : narrative}
        </Typography>
      )}

      <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mb: 1.5 }}>
        {model.intent && (
          <Chip size="small" label={`${t("chat.forecast.intent")}: ${model.intent}`} variant="outlined" />
        )}
        {model.method && model.method.length <= 48 && (
          <Chip size="small" label={`${t("chat.forecast.method")}: ${model.method}`} variant="outlined" />
        )}
        {model.method && model.method.length > 48 && (
          <Chip size="small" label={`${t("chat.forecast.method")}: …`} variant="outlined" />
        )}
        {model.indexName && (
          <Chip size="small" label={`${t("chat.forecast.index")}: ${model.indexName}`} variant="outlined" />
        )}
        {model.timeField && (
          <Chip
            size="small"
            label={`${t("chat.forecast.timeField")}: ${model.timeField}`}
            variant="outlined"
          />
        )}
        {model.direction && (
          <Chip
            size="small"
            color="info"
            label={`${t("chat.forecast.direction")}: ${model.direction}`}
          />
        )}
      </Stack>

      {predicted != null && (
        <Box
          sx={{
            mb: 1.5,
            px: 1.5,
            py: 1.25,
            borderRadius: 1.5,
            border: "1px solid",
            borderColor: darkMode ? "rgba(246,189,22,0.35)" : "rgba(246,189,22,0.55)",
            bgcolor: darkMode ? "rgba(246,189,22,0.08)" : "rgba(246,189,22,0.12)",
          }}
        >
          <Typography variant="caption" color="text.secondary" display="block">
            {predictedLabel}
          </Typography>
          <Typography variant="h5" fontWeight={700} sx={{ lineHeight: 1.2 }}>
            {formatNumber(predicted)}
          </Typography>
        </Box>
      )}

      {option && (
        <Box sx={{ width: "100%", height: layout.height, minWidth: 0, mb: 1.5 }}>
          <ReactECharts
            option={option}
            style={{ height: "100%", width: "100%" }}
            notMerge
            opts={{ renderer: "canvas" }}
          />
        </Box>
      )}

      <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
        {t("chat.forecast.seriesTableTitle")}
      </Typography>
      <Table data-no-stack size="small" sx={{ mb: 0.5 }}>
        <TableHead>
          <TableRow>
            <TableCell>{t("chat.forecast.periodColumn")}</TableCell>
            <TableCell align="right">{t("chat.forecast.valueColumn")}</TableCell>
            <TableCell>{t("chat.forecast.kindColumn")}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {model.points.map((p) => (
            <TableRow key={`${p.kind}-${p.key}`}>
              <TableCell>{p.key}</TableCell>
              <TableCell align="right">{formatNumber(p.value)}</TableCell>
              <TableCell>
                {p.kind === "forecast"
                  ? t("chat.forecast.forecastSeries")
                  : t("chat.forecast.historySeries")}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {model.method && (
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
          {t("chat.forecast.methodDetail")}: {model.method}
        </Typography>
      )}
    </Box>
  );
}
